<template>
  <section
    v-if="isEditing || isVisible"
    :id="`${$parent.$options.name}-${instanceId}`"
    class="base-module"
    :class="{ 'transition-overflow': transition }"
    :data-module="dataModule"
    :style="{
      '--bg-color': backgroundColor,
      '--title-color': titleColor,
      '--title-weight': titleWeight,
      '--text-color': textColor,
      '--title-size-desk': `${titleSizeDesk}px`,
      '--content-size-desk': `${contentSizeDesk}px`,
      '--title-size-mobile': `${titleSizeMobile}px`,
      '--content-size-mobile': `${contentSizeMobile}px`,
      '--button-color': buttonColor,
      '--button-text-color': buttonTextColor,
      '--padding-top': paddingTop + 'px',
      '--padding-bottom': paddingBottom + 'px',
      '--padding-top-mobile': paddingTopMobile + 'px',
      '--padding-bottom-mobile': paddingBottomMobile + 'px',
    }"
  >
    <div ref="aosWrapper">
      <slot></slot>
    </div>

    <slot name="modal"></slot>
  </section>
</template>

<script>
import weightOptions from '@/helpers/weightOptions.json';

export default {
  name: 'BaseModule',
  inheritAttrs: false,
  props: {
    instanceId: {
      type: String,
      default: '',
    },
    dataModule: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    titleWeight: {
      type: String,
      default: '300',
    },
    subtitle: {
      type: String,
      default: '',
    },
    showOnDesk: {
      type: Boolean,
      default: true,
    },
    showOnMobile: {
      type: Boolean,
      default: true,
    },
    backgroundColor: {
      type: String,
      default: '#f2f0f0',
    },
    titleColor: {
      type: String,
      default: '#333333',
    },
    textColor: {
      type: String,
      default: '#575757',
    },
    titleSizeDesk: {
      type: Number,
      default: 45,
    },
    titleSizeMobile: {
      type: Number,
      default: 24,
    },
    contentSizeDesk: {
      type: Number,
      default: 16,
    },
    contentSizeMobile: {
      type: Number,
      default: 16,
    },
    buttonColor: {
      type: String,
      default: '',
    },
    buttonTextColor: {
      type: String,
      default: '#ffffff',
    },
    paddingTop: {
      type: Number,
      default: 96,
    },
    paddingBottom: {
      type: Number,
      default: 96,
    },
    paddingTopMobile: {
      type: Number,
      default: 56,
    },
    paddingBottomMobile: {
      type: Number,
      default: 56,
    },
    transition: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isDeviceMobile: window.innerWidth < 768,
    };
  },
  computed: {
    isEditing() {
      return this.$route.name === 'editor';
    },
    isVisible() {
      return this.isDeviceMobile ? this.showOnMobile : this.showOnDesk;
    },
  },
  mounted() {
    if (this.$refs.aosWrapper) {
      const { dataset } = this.$refs.aosWrapper;
      const route = this.$route.name;

      if (this.transition && route !== 'editor') {
        dataset.aos = this.transition;
        dataset.aosDuration = 1000;
        dataset.aosAnchorPlacement = 'top-center';
        dataset.aosOnce = true;
      }
    }
  },
  metadata: {
    tabs: [
      {
        label: 'Conteúdo',
        name: 'content',
        groups: [
          {
            name: 'text',
            label: 'Textos',
            fields: [
              {
                name: 'title',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'titleWeight',
                type: 'VueSelect',
                label: 'Peso do título',
                default: '300',
                options: weightOptions,
                isEnabled({ title }) {
                  return title;
                },
              },
              {
                name: 'subtitle',
                type: 'ShortText',
                label: 'Subtítulo',
              },
            ],
          },
          {
            name: 'visibility',
            label: 'Visibilidade',
            fields: [
              {
                name: 'showOnDesk',
                type: 'Checkbox',
                label: 'Exibir no desktop',
              },
              {
                name: 'showOnMobile',
                type: 'Checkbox',
                label: 'Exibir no mobile',
              },
            ],
          },
        ],
      },
      {
        label: 'Aparência',
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor do fundo',
              },
              {
                name: 'titleColor',
                type: 'ColorPicker',
                label: 'Cor do título',
              },
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor do texto',
              },
              {
                name: 'buttonColor',
                type: 'ColorPicker',
                label: 'Cor dos botões',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor do texto dos botões',
              },
            ],
          },
          {
            name: 'fonts',
            label: 'Fontes',
            fields: [
              {
                name: 'titleSizeDesk',
                type: 'Range',
                label: 'Título desktop',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'titleSizeMobile',
                type: 'Range',
                label: 'Título mobile',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'contentSizeDesk',
                type: 'Range',
                label: 'Parágrafo desktop',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'contentSizeMobile',
                type: 'Range',
                label: 'Parágrafo mobile',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
          {
            name: 'padding',
            label: 'Espaçamento do módulo',
            fields: [
              {
                name: 'paddingTop',
                label: 'Espaçamento Superior',
                type: 'Range',
                min: 0,
                max: 200,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'paddingBottom',
                label: 'Espaçamento Inferior',
                type: 'Range',
                min: 0,
                max: 200,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
          {
            name: 'paddingMobile',
            label: 'Espaçamento do módulo em mobile',
            fields: [
              {
                name: 'paddingTopMobile',
                label: 'Espaçamento Superior',
                type: 'Range',
                min: 0,
                max: 200,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'paddingBottomMobile',
                label: 'Espaçamento Inferior',
                type: 'Range',
                min: 0,
                max: 200,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
          {
            name: 'transitions',
            label: 'Animações',
            fields: [
              {
                name: 'transition',
                type: 'VueSelect',
                label: 'Direção da animação',
                options: [
                  {
                    label: 'Sem animação',
                    value: '',
                  },
                  {
                    label: 'Vindo da esquerda',
                    value: 'fade-right',
                  },
                  {
                    label: 'Vindo da direita',
                    value: 'fade-left',
                  },
                  {
                    label: 'Vindo de cima',
                    value: 'fade-down',
                  },
                  {
                    label: 'Vindo de baixo',
                    value: 'fade-up',
                  },
                  {
                    label: 'Vindo de baixo/esquerda',
                    value: 'fade-up-right',
                  },
                  {
                    label: 'Vindo de baixo/direita',
                    value: 'fade-up-left',
                  },
                  {
                    label: 'Vindo de cima/esquerda',
                    value: 'fade-down-right',
                  },
                  {
                    label: 'Vindo de cima/direita',
                    value: 'fade-down-left',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style lang="scss">
.base-module {
  background: var(--bg-color, #f2f0f0);

  > div {
    width: 100%;
  }

  &.transition-overflow {
    overflow: hidden; /* Fallback */
    overflow: clip;
  }

  .base-title-fontsize {
    font-size: var(--title-size-desk, 45px);

    .tit,
    .tit-g {
      font-size: 1em;
    }

    .sub {
      font-size: 0.66em;
    }
  }

  .tit,
  .tit-g {
    color: var(--title-color, $lb-dark-gray);
    font-weight: var(--title-weight, 300);
  }

  .sub {
    color: var(--title-color, $lb-dark-gray);
    opacity: 0.7;
  }

  .txt {
    color: var(--text-color, $lb-medium-gray);
    font-size: var(--content-size-desk, $size-normal);
  }

  .cta {
    color: var(--button-text-color, $lb-white);
    background-color: var(--button-color, var(--primary-color));

    &.vazado {
      color: var(--button-color, var(--primary-color));
      background-color: transparent;
      border: 2px solid var(--button-color, var(--primary-color));
    }
  }

  .base-content-fontsize {
    font-size: var(--content-size-desk, $size-normal);

    .cta {
      font-size: var(--content-size-desk, $size-normal);
    }
  }

  @include media-breakpoint-down(sm) {
    header {
      &:first-child {
        font-size: var(--title-size-mobile, 24px);

        .tit,
        .tit-g {
          font-size: 1em;
        }

        .sub {
          font-size: 0.83em;
        }
      }
    }

    .base-content-fontsize {
      font-size: var(--content-size-mobile, $size-normal);

      .cta {
        font-size: var(--content-size-mobile, $size-normal);
      }
    }

    .txt {
      font-size: var(--content-size-mobile, $size-normal);
    }
  }
}
</style>
